import React, { useCallback, useEffect, useMemo } from "react";
import { useStores } from "../../../hooks/useStores";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Link as CLink, HStack, Button } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { Container } from "../styled";
import { VirtualizedTable } from "../../../components/VirtualizedTable";
import moment from "moment";
import { User } from "../../../types";
import api from "../../../services/api";
import dayjs from "dayjs";

export const TabSubscriptionCancellation: React.FC = observer(() => {
  const { clientsStore } = useStores();
  const { t } = useTranslation();

  useEffect(() => {
    clientsStore.getClientsSubscriptionCancellation();
  }, []);

  const columns = useMemo(
    () => [
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.surname"),
        dataKey: "surname",
        sorter: (a: { surname: string }, b: { surname: any }) =>
          a.surname.localeCompare(b.surname),
        render: (surname: string, user: User) => {
          return (
            <CLink as={Link} to={`/admin/clients/${user.id}`} color="purple">
              {surname}
            </CLink>
          );
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.name"),
        dataKey: "name",
        sorter: (a: { name: string }, b: { name: any }) =>
          a.name.localeCompare(b.name),
        render: (name: string, user: User) => {
          return (
            <CLink as={Link} to={`/admin/clients/${user.id}`} color="purple">
              {name}
            </CLink>
          );
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.email"),
        dataKey: "email",
        sorter: (a: { email: string }, b: { email: any }) =>
          a.email.localeCompare(b.email),
        render: (email: string) => {
          return email;
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.registrationDate"),
        dataKey: "createdAt",
        sorter: (
          a: { createdAt: moment.MomentInput },
          b: { createdAt: moment.MomentInput }
        ) => moment.utc(a.createdAt).diff(moment.utc(b.createdAt)),
        render: (createdAt: string) => {
          return moment(createdAt).format("DD/MM/YYYY");
        },
      },
      {
        width: 80,
        flexGrow: 1,
        label: t("screens.client.customerTableHead.cancellationDate"),
        dataKey: "subscription_disactivation_date",
        sorter: (
          a: { subscription_disactivation_date: moment.MomentInput },
          b: { subscription_disactivation_date: moment.MomentInput }
        ) =>
          moment
            .utc(a.subscription_disactivation_date)
            .diff(moment.utc(b.subscription_disactivation_date)),
        render: (subscription_disactivation_date: string) => {
          return moment(subscription_disactivation_date).format("DD/MM/YYYY");
        },
      },
    ],
    [t]
  );

  const handleDownloadReport = useCallback(async () => {
    const rows = [
      [
        t("screens.client.customerTableHead.surname").toUpperCase(),
        t("screens.client.customerTableHead.name").toUpperCase(),
        t("screens.client.customerTableHead.email").toUpperCase(),
        t("screens.client.customerTableHead.registrationDate").toUpperCase(),
        t("screens.client.customerTableHead.cancellationDate").toUpperCase(),
      ],
    ];

    for (let customer of clientsStore.clientsSubscriptionCancellation) {
      rows.push([
        customer.surname,
        customer.name,
        customer.email,
        moment(customer.createdAt).format("DD/MM/YYYY"),
        moment(customer.subscription_disactivation_date).format("DD/MM/YYYY"),
      ]);
    }

    const blob = await api.generateExportFile(rows);

    if (blob) {
      //  Create blob link to download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${dayjs().format("DD-MM-YYYY")}_export_abbonamenti_cancellati.xlsx`
      );

      //  Append to html page
      document.body.appendChild(link);
      // Force download
      link.click();
      // Clean up and remove the link
      link.parentNode?.removeChild(link);
    }
  }, [clientsStore.clientsSubscriptionCancellation, t]);

  return (
    <Container>
      <HStack justifyContent="flex-end" w="100%">
        <Button
          colorScheme={"purple"}
          variant={"ghost"}
          onClick={handleDownloadReport}
        >
          Scarica report
        </Button>
      </HStack>
      <div
        style={{
          flex: "1 1 auto",
          width: "100%",
          height: `${
            (clientsStore.clientsSubscriptionCancellation.length + 1) * 50
          }px`,
          display: "flex",
          marginTop: "20px",
        }}
      >
        <VirtualizedTable
          rowCount={clientsStore.clientsSubscriptionCancellation.length}
          rowGetter={({ index }) =>
            clientsStore.clientsSubscriptionCancellation[index]
          }
          columns={columns}
          rowStyle={(row) =>
            row.index % 2 === 0 ? { background: "#EDF1FB" } : {}
          }
        />
      </div>
    </Container>
  );
});
